import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewEncapsulation,
} from '@angular/core';
import { DEFAULT_CURRENCY_CODE } from '@constants/currency';
import { CurrencyModel, IncoTermModel, ShipmentModel, ValidationMessageModel } from '@data-layer/models/common';
import { EntrySummaryService } from '@services/entry-summary.service';
import { Observable, ReplaySubject } from 'rxjs';
import { switchMap, takeUntil, tap } from 'rxjs/operators';
import { enumChargesDistributionType, enumEntrySummaryState, enumTransportationMode } from '@data-layer/enums/entry-summaries';
import { EntrySummaryStateService, EsLabelsService, EsStepsService } from '@shared/es-mutual/services';
import { DocumentCreateModel, DocumentModel } from '@data-layer/models/documents';
import {
  enumDocumentRelatedEntityType,
  enumDocumentRootEntityType,
  enumDocumentState,
  enumDocumentType,
} from '@data-layer/enums/documents';
import { DocumentsService } from '@services/documents.service';
import { CompaniesService } from '@services/companies.service';
import { CompanyModel } from '@data-layer/models/companies';
import { enumEntityModelCode } from '@app/data-layer/enums/entities';
import { EntityModel } from '@data-layer/models/entities';
import { DESC_ENTITY } from '@constants/desc-entity';
import { TranslateService } from '@ngx-translate/core';
import { EntrySummaryValidationHelper } from '@helpers/entry-summary-validation.helper';
import {
  BUYER_MISSING,
  CONSIGNEE_MISSING,
  FREIGHT_COST_LESS_THAN_LINES_COUNT,
  FREIGHT_COST_MISSING,
  HOUSE_BILL_CARRIER_WRONG_LENGTH,
  MASTER_AIR_BILL_PREFIX_MISSING,
  MASTER_BILL_CARRIER_MISSING,
  MASTER_BILL_CARRIER_WRONG_LENGTH,
  MASTER_BILL_NUMBER_MISSING,
  QUANTITY_MISSING,
  QUANTITY_UNIT_MISSING,
  SELLER_MISSING,
} from '@constants/entity-validation-messages';
import { EntrySummaryModel, InvoiceModel } from '@data-layer/models/entry-summaries';
import { IEsDocumentStateUpdate } from '@shared/es-mutual/interfaces';
import { UntypedFormGroup } from '@angular/forms';
import { blMissingFields, blMissingFieldsSpec, blBuyerMissingFields } from '@shared/es-mutual/const';
import { UserBetaFlagModel } from '@app/data-layer/models/user';
import { enumUserBetaFlag } from '@app/data-layer/enums/user';
import { HtsLineStatusHelper } from '@shared/hts-assistant/helpers/hts-line-status.helper';
import { EntrySummaryValidationService } from '@services/entry-summary-validation.service';
import { ApplicationModeService } from '@services/application-mode.service';
import { enumApplicationMode } from '@enum/enumApplicationMode';
import { enumPaymentState } from '@app/data-layer/enums/payments';

@Component({
  selector: 'es-bill-of-lading-view-desktop',
  template: `
    <section class="es-bl">
      <header class="es-bl-header">
        <div class="d-flex align-items-center">
          <h3>{{ 'DOCUMENT_TYPE.BILL_OF_LADING' | translate }}{{ blOrdinalSuffix }}</h3>

          <div class="es-bl-header-details" *ngIf="!isHasEsOcrRedesign">
            <div class="es-bl-header-details-col">
              <div class="es-bl-header-details-label">{{ 'FORM_LABEL.TOTAL_QUANTITY' | translate }}</div>
              <div class="es-bl-header-details-value">
                <span *ngIf="shipment.quantity || shipment.quantityUnit">
                  {{ shipment.quantity ? shipment.quantity : '' }} {{ shipment.quantityUnit ? shipment.quantityUnit : '' }}
                </span>

                <span class="text-error" *ngIf="blQuantityErrorText || blQuantityUnitErrorText">
                  {{ blQuantityErrorText | translate }}
                  {{ blQuantityUnitErrorText | translate }}
                </span>
              </div>
            </div>

            <div class="es-bl-header-details-col" *ngIf="shipment.invoices">
              <div class="es-bl-header-details-label">
                {{ 'DASHBOARD_TITLE.INVOICES' | translate }}
              </div>
              <div class="es-bl-header-details-value">{{ shipment.invoices.length }}</div>
            </div>

            <div class="es-bl-header-details-col">
              <div class="es-bl-header-details-label">
                {{ 'ENTRY_SUMMARY.BL_ENTERED_VALUE' | translate }}
              </div>
              <div class="es-bl-header-details-value">{{ blEnteredValue | currency : DEFAULT_CURRENCY_CODE : 'symbol' : '1.2-2' }}</div>
            </div>
          </div>
        </div>

        <div class="es-bl-header-actions" *ngIf="!isHasEsOcrRedesign">
          <ng-container *ngIf="(isCanEdit && isCanAdd && !isTruck) || isCanRemove">
            <ng-container *ngTemplateOutlet="blDropdown"></ng-container>
          </ng-container>
          <div>
            <button type="button" class="btn btn-link btn-arrow ml-2" (click)="openBLBody()">
              <i class="fa" [ngClass]="{ 'fa-chevron-up': isBodyOpen, 'fa-chevron-down': !isBodyOpen }"></i>
            </button>
          </div>
        </div>

        <div class="es-bl-header-actions with-gap" *ngIf="isHasEsOcrRedesign">
          <button
            nz-tooltip
            nzTooltipColor="#ffffff"
            nzTooltipPlacement="top"
            nzTooltipOverlayClassName="normalize"
            [nzTooltipTitle]="'ADD_ANOTHER_BILL_OF_LADING' | translate"
            *ngIf="!isTruck && !isBlDetailsEmpty && isShowInfo"
            type="button"
            class="es-bl-header-actions__button btn btn-link"
            (click)="addNewBl.emit()"
          >
            <i class="fa fa-plus"></i>
          </button>
          <button
            nz-tooltip
            nzTooltipColor="#ffffff"
            nzTooltipPlacement="top"
            nzTooltipOverlayClassName="normalize"
            [nzTooltipTitle]="'DELETE_BILL_OF_LADING' | translate"
            *ngIf="isCanRemove"
            type="button"
            class="es-bl-header-actions__button btn btn-link"
            (click)="isBLConfirmModalOpen = true"
          >
            <i class="fa fa-trash"></i>
          </button>
        </div>
      </header>
      <div class="es-bl-body is-view" *ngIf="isBodyOpen">
        <!-- 1st BL Row -->
        <div class="es-bl-body-info">
          <div class="es-bl-body-info-wrapper" [ngClass]="{ 'no-border': isHasEsOcrRedesign }">
            <div class="es-bl-body-info-details">
              <header class="es-bl-body-info-details-header">
                <h5 [ngClass]="{ 'text-error': isHasEsOcrRedesign && isAnyBlDetailsInvalid }">
                  {{ 'LABELS.BILL_OF_LADING_DETAILS' | translate }}
                </h5>

                <button type="button" class="btn btn-link d-print-none ml-2" *ngIf="canEditBl ?? isCanEdit" (click)="editBl.emit()">
                  <svg width="19" height="19" viewBox="0 0 19 19" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M0 14.2525V18.0025H3.75L14.81 6.9425L11.06 3.1925L0 14.2525ZM17.71 4.0425C18.1 3.6525 18.1 3.0225 17.71 2.6325L15.37 0.2925C14.98 -0.0975 14.35 -0.0975 13.96 0.2925L12.13 2.1225L15.88 5.8725L17.71 4.0425Z"
                      fill="currentColor"
                    />
                  </svg>
                </button>
              </header>

              <div class="es-bl-body-info-details-body" [ngClass]="{ redesign: isHasEsOcrRedesign }">
                <div class="es-bl-body-info-details-col" [ngClass]="{ 'es-bl-body-info-details-col-half': isTruck }">
                  <div class="es-bl-body-info-details-label">{{ currentLabelsData.titleMaster | translate }}</div>
                  <div
                    class="es-bl-body-info-details-value"
                    *ngIf="shipment.masterBill?.carrier; else emptyInfo"
                    [ngClass]="{ 'text-error': hasBlMasterCarrierErrorText }"
                  >
                    {{ shipment.masterBill?.carrier }}
                  </div>
                </div>
                <div class="es-bl-body-info-details-col" [ngClass]="{ 'es-bl-body-info-details-col-half': isTruck }">
                  <div class="es-bl-body-info-details-label">
                    {{ currentLabelsData.titleNumber | translate }}
                    <span *ngIf="isTruck">({{ 'LABELS.OPTIONAL' | translate }})</span>
                  </div>
                  <div class="es-bl-body-info-details-value" *ngIf="shipment.masterBill?.number || isTruck; else emptyInfo">
                    {{ shipment.masterBill?.number }}
                  </div>
                </div>

                <div class="es-bl-body-info-details-col" *ngIf="!isRail && !isTruck">
                  <div class="es-bl-body-info-details-label">{{ currentLabelsData.titleHouse | translate }}</div>
                  <div
                    class="es-bl-body-info-details-value"
                    *ngIf="shipment.houseBill?.carrier; else shipment.houseBill?.number && !shipment.houseBill?.carrier && emptyInfo"
                    [ngClass]="{ 'text-error': hasBlHouseCarrierErrorText }"
                  >
                    {{ shipment.houseBill?.carrier }}
                  </div>
                </div>
                <div class="es-bl-body-info-details-col" *ngIf="!isRail && !isTruck">
                  <div class="es-bl-body-info-details-label">{{ currentLabelsData.titleNumber | translate }}</div>
                  <div
                    class="es-bl-body-info-details-value"
                    *ngIf="shipment.houseBill?.number; else shipment.houseBill?.carrier && !shipment.houseBill?.number && emptyInfo"
                  >
                    {{ shipment.houseBill?.number }}
                  </div>
                </div>
              </div>

              <div class="es-bl-body-info-details-body it-number" *ngIf="entrySummary.portOfEntryCode !== entrySummary.portOfUnladingCode">
                <div class="es-bl-body-info-details-col" [ngClass]="{ 'es-bl-body-info-details-col-half': isTruck }">
                  <div class="es-bl-body-info-details-label">{{ 'FORM_LABEL.IT_NUMBER' | translate }}</div>
                  <div class="es-bl-body-info-details-value" *ngIf="shipment.itNumber; else emptyInfo">
                    {{ shipment.itNumber }}
                  </div>
                </div>
                <div class="es-bl-body-info-details-col" [ngClass]="{ 'es-bl-body-info-details-col-half': isTruck }">
                  <div class="es-bl-body-info-details-label">{{ 'FORM_LABEL.IT_DATE' | translate }}</div>
                  <div class="es-bl-body-info-details-value" *ngIf="shipment.itDate; else emptyInfo">
                    {{ shipment.itDate | customDateByUtc }}
                  </div>
                </div>
              </div>

              <div class="es-bl-body-info-details-body">
                <div class="text-error" *ngFor="let message of shipmentValidationMessages">
                  {{ 'BACK_ERROR.' + message.text | translate }}
                </div>
              </div>
            </div>
          </div>

          <div class="es-bl-body-info-files" *ngIf="isShowBlDocument && (!isHasEsOcrRedesign || (isSupportOrAdmin && isActiveSupportMode))">
            <document-upload-desktop
              [id]="'es-bl-' + shipment.id + 'documents'"
              [attachmentName]="'LABELS.BILL_OF_LADING'"
              [attachmentTypeName]="'LABELS.BILL_OF_LADING' | translate"
              [attachmentHint]="'LABELS.BILL_OF_LADING_HINT' | translate"
              [uploadedDocument]="blDoc"
              [canUploadDocuments]="isCanEdit || isManagement"
              [canRemoveDocuments]="isCanEdit"
              [isRequired]="isRequiredDocument"
              [isStretched]="true"
              (upload)="uploadDocumentCall(blDoc, $event)"
              (remove)="removeDocument(blDoc.id)"
              (stateUpdate)="onDocumentStateUpdate(blDoc.id, $event)"
            ></document-upload-desktop>
          </div>
        </div>

        <!-- 2nd BL Row -->
        <div class="es-bl-body-info with-top-border" *ngIf="isHasEsOcrRedesign">
          <div class="es-bl-body-info-wrapper" [ngClass]="{ 'no-border': isHasEsOcrRedesign }">
            <div class="es-bl-body-info-details no-hover">
              <div class="es-bl-body-info-details-body redesign p-0">
                <div class="es-bl-body-info-details-col is-editable flex-fill with-right-border">
                  <div class="d-flex justify-content-between">
                    <div class="d-flex flex-column">
                      <div class="es-bl-body-info-details-label">{{ 'FORM_LABEL.TOTAL_QUANTITY' | translate }}</div>
                      <div class="es-bl-header-details-value">
                        <ng-container *ngIf="isHasEsOcrRedesign">
                          <span *ngIf="!shipment?.quantity && !shipment?.quantityUnit; else defaultQuantityView">-----</span>

                          <ng-template #defaultQuantityView>
                            <span>
                              {{ shipment?.quantity ? shipment?.quantity : '-----' }}
                              {{ shipment?.quantityUnit ? shipment?.quantityUnit : '-----' }}
                            </span>
                          </ng-template>
                        </ng-container>

                        <ng-container *ngIf="!isHasEsOcrRedesign">
                          <span *ngIf="shipment.quantity || shipment.quantityUnit">
                            {{ shipment.quantity ? shipment.quantity : '' }} {{ shipment.quantityUnit ? shipment.quantityUnit : '' }}
                          </span>

                          <span class="text-error" *ngIf="blQuantityErrorText || blQuantityUnitErrorText">
                            {{ blQuantityErrorText | translate }}
                            {{ blQuantityUnitErrorText | translate }}
                          </span>
                        </ng-container>
                      </div>
                    </div>

                    <button type="button" class="btn btn-link d-print-none mr-4" *ngIf="canEditBl ?? isCanEdit" (click)="editBl.emit()">
                      <svg width="19" height="19" viewBox="0 0 19 19" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                        <path
                          d="M0 14.2525V18.0025H3.75L14.81 6.9425L11.06 3.1925L0 14.2525ZM17.71 4.0425C18.1 3.6525 18.1 3.0225 17.71 2.6325L15.37 0.2925C14.98 -0.0975 14.35 -0.0975 13.96 0.2925L12.13 2.1225L15.88 5.8725L17.71 4.0425Z"
                          fill="currentColor"
                        />
                      </svg>
                    </button>
                  </div>
                </div>

                <div class="es-bl-body-info-details-col pl-3 flex-fill with-right-border">
                  <div class="es-bl-body-info-details-label">
                    {{ 'DASHBOARD_TITLE.INVOICES' | translate }}
                  </div>
                  <div class="es-bl-header-details-value flex-row-between">
                    {{ shipment.invoices.length }}
                    <img
                      *ngIf="(isShowAddNewInvoice || !shipment?.invoices?.length) && isCanEdit"
                      src="assets/icons/icon_plus_black_sm.svg"
                      alt="add invoice"
                      (click)="onAddInvoice()"
                    />
                  </div>
                </div>

                <div class="es-bl-body-info-details-col pl-3 flex-fill">
                  <div class="es-bl-body-info-details-label">
                    {{ 'ENTRY_SUMMARY.BL_ENTERED_VALUE' | translate }}
                  </div>
                  <div class="es-bl-header-details-value">
                    {{ blEnteredValue | currency : DEFAULT_CURRENCY_CODE : 'symbol' : '1.2-2' }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="es-bl-body-entities">
          <div class="es-bl-body-entities-wrapper">
            <desktop-summary-entity
              [entity]="shipment?.seller"
              [isShowIdentifier]="false"
              [entityCode]="enumEntityModelCode.SELLER"
              [isEditMode]="canEditShipmentEntities ?? isCanEdit"
              [isLastInRow]="true"
              (edit)="editSeller.emit()"
            ></desktop-summary-entity>
          </div>

          <div class="es-bl-body-entities-wrapper">
            <desktop-summary-entity
              [entity]="shipment?.consignee"
              [isShowIdentifier]="true"
              [entityCode]="enumEntityModelCode.CONSIGNEE"
              [isEditMode]="canEditShipmentEntities ?? isCanEdit"
              [sameAs]="isSameAs(shipment?.consignee, entrySummary.importer)"
              (edit)="editConsignee.emit()"
            ></desktop-summary-entity>
          </div>

          <div class="es-bl-body-entities-wrapper">
            <desktop-summary-entity
              [entity]="shipment?.buyer"
              [isShowIdentifier]="true"
              [entityCode]="enumEntityModelCode.BUYER"
              [isEditMode]="canEditShipmentEntities ?? isCanEdit"
              [isLastInRow]="true"
              [sameAs]="isSameAs(shipment?.buyer, entrySummary.importer)"
              (edit)="editBuyer.emit()"
            ></desktop-summary-entity>
          </div>

          <div class="es-bl-body-entities-wrapper is-last">
            <div class="es-bl-body-entities-terms">
              <header class="es-bl-body-entities-terms-header">
                <h5 [ngClass]="{ 'text-error': isHasEsOcrRedesign && !shipment.freightCost }">
                  {{ 'LABELS.SHIPPING_TERMS_OF_SALE' | translate }}
                </h5>

                <button
                  type="button"
                  class="btn btn-link d-print-none ml-2"
                  *ngIf="(canEditTermsOfSale ?? isCanEdit) && !isAllTermsFieldsMissing"
                  (click)="editTermsOfSale.emit()"
                >
                  <svg width="19" height="19" viewBox="0 0 19 19" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M0 14.2525V18.0025H3.75L14.81 6.9425L11.06 3.1925L0 14.2525ZM17.71 4.0425C18.1 3.6525 18.1 3.0225 17.71 2.6325L15.37 0.2925C14.98 -0.0975 14.35 -0.0975 13.96 0.2925L12.13 2.1225L15.88 5.8725L17.71 4.0425Z"
                      fill="currentColor"
                    />
                  </svg>
                </button>
              </header>

              <div
                class="es-bl-body-entities-terms-body"
                [ngClass]="{ 'flex-grow-1': isAllTermsFieldsMissing, redesign: isHasEsOcrRedesign }"
              >
                <div *ngIf="isAllTermsFieldsMissing && (canEditTermsOfSale ?? isCanEdit)" class="info-shipment-item__missing-fields">
                  <div class="info-shipment-item__missing-fields__edit-button" (click)="editTermsOfSale.emit()">
                    <img
                      class="info-shipment-item__missing-fields__edit-button__icon"
                      src="assets/icons/icon_edit_gray_md.svg"
                      alt="edit"
                    />
                  </div>
                </div>

                <ng-container *ngIf="!isAllTermsFieldsMissing">
                  <div class="es-bl-body-entities-terms-row">
                    <div class="es-bl-body-entities-terms-label">{{ 'FORM_LABEL.CHARGES_DISTRIBUTION_TYPE_DESKTOP' | translate }}</div>
                    <div class="es-bl-body-entities-terms-value">
                      {{ 'CHARGES_DISTRIBUTION_TYPE.' + enumChargesDistributionType[shipment.chargesDistributionType] | translate }}
                    </div>
                  </div>

                  <div class="es-bl-body-entities-terms-row">
                    <div class="es-bl-body-entities-terms-label">
                      <span *ngIf="shipment.chargesDistributionType === enumChargesDistributionType.COLLECT">
                        {{ 'FORM_LABEL.TOTAL_CHARGES' | translate }}
                      </span>
                      <span *ngIf="shipment.chargesDistributionType === enumChargesDistributionType.PREPAID">
                        {{ 'FORM_LABEL.FREIGHT_COST' | translate }}
                      </span>
                    </div>
                    <div class="es-bl-body-entities-terms-value">
                      {{
                        this.isHasEsOcrRedesign && !shipment.freightCost
                          ? '-----'
                          : (shipment.freightCost || 0 | currency : DEFAULT_CURRENCY_CODE : 'symbol' : '1.2-2')
                      }}
                    </div>
                  </div>

                  <div
                    class="es-bl-body-entities-terms-row"
                    *ngIf="shipment.chargesDistributionType === enumChargesDistributionType.PREPAID"
                  >
                    <div class="es-bl-body-entities-terms-label">{{ 'FORM_LABEL.TRANSPORTATION' | translate }}</div>
                    <div class="es-bl-body-entities-terms-value">
                      {{ shipment.transportationCharge || 0 | currency : DEFAULT_CURRENCY_CODE : 'symbol' : '1.2-2' }}
                    </div>
                  </div>

                  <div
                    class="es-bl-body-entities-terms-row"
                    *ngIf="shipment.chargesDistributionType === enumChargesDistributionType.PREPAID"
                  >
                    <div class="es-bl-body-entities-terms-label">{{ 'FORM_LABEL.INSURANCE' | translate }}</div>
                    <div class="es-bl-body-entities-terms-value">
                      {{ shipment.insurance || 0 | currency : DEFAULT_CURRENCY_CODE : 'symbol' : '1.2-2' }}
                    </div>
                  </div>
                </ng-container>

                <ng-container *ngIf="!isHasEsOcrRedesign || (isHasEsOcrRedesign && !isAllTermsFieldsMissing)">
                  <span class="text-error mb-1" *ngFor="let message of termsValidationMessages">
                    {{ 'BACK_ERROR.' + message.text | translate }}
                  </span>
                </ng-container>
              </div>
            </div>
          </div>
        </div>

        <div class="es-bl-body-invoices" *ngIf="!!shipment?.invoices?.length">
          <div class="es-bl-body-invoices-item" *ngFor="let invoice of shipment.invoices; let index = index; trackBy: trackByInvoiceFn">
            <ng-container>
              <es-details-invoice-ocr-card-desktop
                [entrySummary]="entrySummary"
                [referenceNumber]="entrySummary?.referenceNumber"
                [invoice]="invoice"
                [billOfLadingId]="shipment?.id"
                [esId]="entrySummary?.id"
                [isBatchES]="isBatchES"
                [attachedDocuments]="attachedDocuments"
                [transportationMode]="transportationMode"
                [shipmentExportDate]="shipment.exportDate"
                [isCreatingInvoice]="isCreatingInvoice"
                [isEditable]="isCanEdit"
                [canEditInvoiceDetails]="canEditInvoiceDetails"
                [canDeleteInvoice]="canDeleteInvoice"
                [canEditLineItems]="canEditLineItems"
                [isShowFinalize]="isShowFinalize"
                [isFinalizeEnabled]="isFinalizeEnabled"
                (finalize)="finalize.emit({ shipmentId: shipment.id, invoiceId: invoice.id })"
                [canClearInvoice]="canClearInvoice"
                [canEditManufacturer]="canEditManufacturer"
                [currencies]="currencies"
                [currencyRate]="currencyRate"
                [incoterms]="incoterms"
                [invoiceFg]="invoiceFg"
                [isFormForcedOpen]="invoice.id === forceFormOpenedInvoiceId"
                [isCanBulkDelete]="isCanBulkDelete"
                [invoiceOrdinal]="isMultiInvoice ? index + 1 : null"
                (editInvoice)="editInvoice.emit({ invoice: invoice, shipment: shipment })"
                (editMF)="editManufacturer.emit({ invoice: invoice, shipment: shipment })"
                (editCleanMF)="
                  editManufacturer.emit({ invoice: joinedInvoice(invoice, invoiceFg.value), shipment: shipment, isCreating: true })
                "
                (replaceInvoice)="replaceInvoice.emit({ invoiceId: invoice.id, file: $event }); isShowAddInvoiceButton = true"
                (deleteInvoice)="removeInvoice(invoice, index)"
                (addLineItem)="addLineItem.emit({ shipmentId: shipment.id, invoiceId: invoice.id })"
                (editLineItem)="editLineItem.emit({ shipmentId: shipment.id, invoiceId: invoice.id, itemId: $event })"
                (deleteLineItem)="deleteLineItem.emit($event)"
                (linesDeleted)="linesDeleted.emit()"
                (save)="savedInvoice.emit()"
                (updateInvoice)="updateInvoice.emit({ invoice: invoice, shipment: shipment })"
                (clearInvoice)="clearInvoice.emit()"
                (clearDataRequested)="clearInvoiceData.emit({ invoice: invoice, shipment: shipment })"
                (htsOpenSource)="checkInvoicesOpenStates($event, index)"
                (fileUploaded)="fileUploaded.emit()"
                (reviewInvoice)="reviewInvoice.emit({ invoice: invoice, shipment: shipment })"
              ></es-details-invoice-ocr-card-desktop>
            </ng-container>
          </div>
        </div>
      </div>

      <div class="es-bl-inner-container" [ngClass]="{ 'd-none': !isBodyOpen || !isShowAddInvoiceButton }" *ngIf="showInnerContainer">
        <ng-content select="[body]"></ng-content>
      </div>
    </section>

    <ng-template #emptyInfo>
      <ng-container *ngIf="isHasEsOcrRedesign">
        <span class="es-bl-body-info-details-value">-----</span>
      </ng-container>
      <ng-container *ngIf="!isHasEsOcrRedesign">
        <p class="text-error">{{ 'LABELS.MISSING' | translate }}</p>
      </ng-container>
    </ng-template>

    <ng-container *ngIf="isHasEsOcrRedesign">
      <custom-modal [header]="'LABELS.DELETE_BL'" [(open)]="isBLConfirmModalOpen">
        <div>{{ 'DELETE_ITEM_TEXT' | translate }}</div>

        <button footer-action type="button" class="btn btn-lg btn-outline-bordered btn-square ml-2" (click)="isBLConfirmModalOpen = false">
          {{ 'LABELS.NO' | translate | uppercase }}
        </button>

        <button footer-action type="button" class="btn btn-lg btn-square btn-primary" (click)="onDeleteBL()">
          {{ 'LABELS.YES' | translate | uppercase }}
        </button>
      </custom-modal>
    </ng-container>

    <ng-container *ngIf="!isHasEsOcrRedesign">
      <custom-modal [header]="'LABELS.DELETE_BL'" [(open)]="isBLConfirmModalOpen">
        <div>{{ 'DELETE_ITEM_TEXT' | translate }}</div>

        <button footer-action type="button" class="btn btn-primary" (click)="onDeleteBL()">{{ 'LABELS.YES' | translate }}</button>

        <button footer-action type="button" class="btn btn-secondary ml-2" (click)="isBLConfirmModalOpen = false">
          {{ 'LABELS.NO' | translate }}
        </button>
      </custom-modal>
    </ng-container>

    <ng-template #blDropdown>
      <div class="view-bl-menu" dropdown container="body" placement="bottom right">
        <button id="shipment-menu-{{ shipment.id }}" dropdownToggle type="button" class="btn btn-link dropdown-toggle view-bl-menu-btn">
          <i class="fa fa-ellipsis-h"></i>
        </button>
        <ul id="bl-dropdown-{{ shipment.id }}" class="view-bl-menu-list" role="menu" *dropdownMenu>
          <li role="menuitem" class="cursor-pointer" *ngIf="!isTruck" (click)="addNewBl.emit()">
            <div class="view-bl-menu-item">
              <div class="view-bl-menu-item-img-container">
                <img src="assets/icons/icon_plus_circle.svg" class="view-bl-menu-item-img view-bl-menu-item-img-add" />
              </div>
              <span>{{ 'ADD_ANOTHER_BILL_OF_LADING' | translate }}</span>
            </div>
          </li>
          <li role="menuitem" class="cursor-pointer" (click)="isBLConfirmModalOpen = true" *ngIf="isCanRemove">
            <div class="view-bl-menu-item">
              <div class="view-bl-menu-item-img-container">
                <img src="assets/icons/trash_gray.svg" class="view-bl-menu-item-img view-bl-menu-item-img-delete" />
              </div>
              <span>{{ 'DELETE_BILL_OF_LADING' | translate }}</span>
            </div>
          </li>
          <li role="menuitem" class="cursor-pointer" (click)="addNewInvoice.emit(shipment?.id)">
            <div class="view-bl-menu-item">
              <div class="view-bl-menu-item-img-container">
                <img src="assets/icons/icon_plus_circle.svg" class="view-bl-menu-item-img view-bl-menu-item-img-add" />
              </div>
              <span>{{ 'ADD_ANOTHER_INVOICE' | translate }}</span>
            </div>
          </li>
        </ul>
      </div>
    </ng-template>
  `,
  styleUrls: ['./es-bill-of-lading-view-desktop.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EsBillOfLadingViewDesktopComponent implements OnInit, OnChanges, OnDestroy {
  @Input() public shipment: ShipmentModel;
  @Input() public transportationMode: enumTransportationMode = enumTransportationMode.OCEAN;
  @Input() public entrySummary: EntrySummaryModel;
  @Input() public isCanEdit: boolean = true;
  @Input() public isCanRemove: boolean = false;
  @Input() public isCanAdd: boolean = true;
  @Input() public isRequiredDocument: boolean = true;
  @Input() public isRequiredInvoiceDocument: boolean = true;
  @Input() public hasSingleExportData: boolean = false;
  @Input() public company: CompanyModel;
  @Input() public incoterms: IncoTermModel[] = [];
  @Input() public currencies: CurrencyModel[] = [];
  @Input() public currencyRate: number;
  @Input() public isBodyOpen: boolean = true;
  @Input() public isBLNewlyCreated: boolean = false;
  @Input() public newlyCreatedInvoicesIds: number[] = [];
  @Input() public isManagement: boolean;
  @Input() public showInnerContainer: boolean = true;
  @Input() public isCreatingInvoice: boolean;
  @Input() public invoiceFg: UntypedFormGroup;
  @Input() public isBatchES: boolean;
  @Input() public isShowAddNewInvoice: boolean;
  @Input() public isShowBlDocument: boolean = true;
  // Additive granular edit affordance: when undefined it falls back to `isCanEdit` (so user-side /
  // admin usages are unchanged); the broker page sets it to enable terms-of-sale editing while the
  // rest of the BL view stays read-only. More granular flags are added per slice as editing grows.
  @Input() public canEditTermsOfSale: boolean;
  @Input() public canEditInvoiceDetails: boolean;
  @Input() public canDeleteInvoice: boolean;
  @Input() public canEditLineItems: boolean;
  @Input() public canEditBl: boolean;
  @Input() public canClearInvoice: boolean;
  @Input() public canEditShipmentEntities: boolean;
  @Input() public canEditManufacturer: boolean;
  // Broker review page: id of the invoice whose inline manual-entry form should be force-opened
  // after a parent-driven "Clear data". Matched per-card below. Unbound → user-side unaffected.
  @Input() public forceFormOpenedInvoiceId: number;
  // Gate for the line items multi-select / bulk-delete UI (client + broker pages); admin leaves it unbound → false.
  @Input() public isCanBulkDelete: boolean = false;
  // #13384: "Finalize" (Validate HTS codes) in every invoice footer; shown while a line of THIS BL still needs classification.
  @Input() public isShowFinalize: boolean;

  public get isFinalizeEnabled(): boolean {
    return !!this.shipment && HtsLineStatusHelper.countNeedsClassification(this.shipment) > 0;
  }

  public get attachedDocuments(): DocumentModel[] {
    return this._attachedDocuments;
  }

  @Input()
  public set attachedDocuments(value: DocumentModel[]) {
    this._attachedDocuments = [...value];
  }

  @Output() public addNewBl: EventEmitter<void> = new EventEmitter<void>();
  @Output() public removeBl: EventEmitter<void> = new EventEmitter<void>();
  @Output() public addLineItem: EventEmitter<{ shipmentId: number; invoiceId: number }> = new EventEmitter<{
    shipmentId: number;
    invoiceId: number;
  }>();
  @Output() public editLineItem: EventEmitter<{ shipmentId: number; invoiceId: number; itemId: number }> = new EventEmitter<{
    shipmentId: number;
    invoiceId: number;
    itemId: number;
  }>();
  @Output() public deleteLineItem: EventEmitter<number> = new EventEmitter<number>();
  @Output() public linesDeleted: EventEmitter<void> = new EventEmitter<void>();
  @Output() public finalize: EventEmitter<{ shipmentId: number; invoiceId: number }> = new EventEmitter<{
    shipmentId: number;
    invoiceId: number;
  }>();
  @Output() public editBl: EventEmitter<void> = new EventEmitter<void>();
  @Output() public editTermsOfSale: EventEmitter<void> = new EventEmitter<void>();
  @Output() public editSeller: EventEmitter<void> = new EventEmitter<void>();
  @Output() public editConsignee: EventEmitter<void> = new EventEmitter<void>();
  @Output() public editBuyer: EventEmitter<void> = new EventEmitter<void>();
  @Output() public editInvoice: EventEmitter<{
    invoice: InvoiceModel;
    shipment: ShipmentModel;
  }> = new EventEmitter<{
    invoice: InvoiceModel;
    shipment: ShipmentModel;
  }>();
  @Output() public removeInvoiceSource: EventEmitter<{
    invoiceId: number;
    shipmentId: number;
  }> = new EventEmitter<{
    invoiceId: number;
    shipmentId: number;
  }>();
  @Output() public editManufacturer: EventEmitter<{
    invoice: InvoiceModel;
    shipment: ShipmentModel;
    isCreating?: boolean;
  }> = new EventEmitter<{
    invoice: InvoiceModel;
    shipment: ShipmentModel;
    isCreating?: boolean;
  }>();
  @Output() public savedInvoice: EventEmitter<void> = new EventEmitter<void>();
  @Output() public documentStateUpdate: EventEmitter<IEsDocumentStateUpdate> = new EventEmitter();
  @Output() public replaceInvoice: EventEmitter<{ invoiceId: number; file: File }> = new EventEmitter<{ invoiceId: number; file: File }>();
  @Output() public updateInvoice: EventEmitter<{
    invoice: InvoiceModel;
    shipment: ShipmentModel;
  }> = new EventEmitter<{
    invoice: InvoiceModel;
    shipment: ShipmentModel;
  }>();
  @Output() public clearInvoice: EventEmitter<void> = new EventEmitter<void>();
  // Broker review page: the invoice card delegates "Clear data" to the parent (its own injected
  // state service is the user-side root one) — bubbled up with the invoice + shipment context.
  @Output() public clearInvoiceData: EventEmitter<{ invoice: InvoiceModel; shipment: ShipmentModel }> = new EventEmitter<{
    invoice: InvoiceModel;
    shipment: ShipmentModel;
  }>();
  @Output() public addNewInvoice: EventEmitter<{ blId: number }> = new EventEmitter<{ blId: number }>();
  @Output() public rerunOCR: EventEmitter<File> = new EventEmitter<File>();
  @Output() public fileUploaded: EventEmitter<void> = new EventEmitter<void>();
  @Output() public reviewInvoice: EventEmitter<{
    invoice: InvoiceModel;
    shipment: ShipmentModel;
  }> = new EventEmitter<{
    invoice: InvoiceModel;
    shipment: ShipmentModel;
  }>();

  public DEFAULT_CURRENCY_CODE: string = DEFAULT_CURRENCY_CODE;
  public enumEntityModelCode = enumEntityModelCode;
  public enumChargesDistributionType = enumChargesDistributionType;
  public enumTransportationMode = enumTransportationMode;

  public blEnteredValue: number;
  // #13499 — 1-based position of this BL inside the ES; null when the ES has a single BL (header shows no "#N")
  public blOrdinal: number = null;

  public currentLabelsData: { titleMaster: string; titleHouse: string; titleCarrier: string; titleNumber: string };

  public _attachedDocuments: DocumentModel[] = [];
  public blDoc: DocumentModel;

  public allValidationMessages: ValidationMessageModel[] = [];
  public shipmentValidationMessages: ValidationMessageModel[] = [];
  public termsValidationMessages: ValidationMessageModel[] = [];
  public isSellerMissed: boolean = false;
  public isConsigneeMissed: boolean = false;
  public isBuyerMissed: boolean = false;
  public buyerValidationMessages: ValidationMessageModel[] = [];
  public isBLConfirmModalOpen: boolean;

  public invoicesOpenStates: boolean[] = [];
  public isShowAddInvoiceButton: boolean = true;
  public isBlDetailsEmpty: boolean;
  private destroy$: ReplaySubject<boolean> = new ReplaySubject<boolean>();

  constructor(
    private entrySummaryService: EntrySummaryService,
    private esLabelsService: EsLabelsService,
    private changeDetector: ChangeDetectorRef,
    private documentsService: DocumentsService,
    private companiesService: CompaniesService,
    private translateService: TranslateService,
    private esStepsService: EsStepsService,
    private applicationModeService: ApplicationModeService,
    private entrySummaryStateService: EntrySummaryStateService,
    private entrySummaryValidationService: EntrySummaryValidationService,
  ) {}

  public ngOnInit(): void {
    this.updateBlEnteredValue();

    if (this.isHasEsOcrRedesign) {
      this.entrySummaryStateService.invoiceSaved$.pipe(takeUntil(this.destroy$)).subscribe((isSaved) => {
        if (!isSaved) {
          return;
        }
        this.updateInvoiceOpenStates();
        this.changeDetector.detectChanges();
      });
    }
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes?.attachedDocuments) {
      this.prepareInvoiceDocuments();
    }
    if (changes?.shipment) {
      this.updateInvoiceOpenStates();
      this.updateBlEnteredValue();
    }
    if (changes?.shipment || changes?.entrySummary) {
      this.updateBlOrdinal();
    }
    if (changes?.transportationMode) {
      // #13414 — BL labels follow the current transportation mode, not only the initial one
      this.updateLabelsData();
    }
    if (changes?.shipment || changes?.transportationMode) {
      this.loadAllValidationMessages();
    }
  }

  public ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  public isSameAs(entity: EntityModel, compareEntity: EntityModel): string {
    if (!entity || !compareEntity) {
      return;
    }

    if (entity?.id === compareEntity?.id) {
      const key = entity.entityCode && DESC_ENTITY[entity.entityCode] ? DESC_ENTITY[entity.entityCode] : DESC_ENTITY['IM'];
      return this.translateService.instant('LABELS.SAME_AS', {
        entityName: this.translateService.instant(key?.titleDesktop),
      });
    }
  }

  public onDocumentStateUpdate(documentId: number, state: enumDocumentState) {
    this.entrySummaryStateService.updateDocumentState(documentId, state);
    this.attachedDocuments = this.attachedDocuments.map((document) => (document.id === documentId ? { ...document, state } : document));
    this.prepareInvoiceDocuments();
    this.documentStateUpdate.emit();
  }

  public uploadDocument(document: DocumentCreateModel, file: File): Observable<any> {
    return this.entrySummaryStateService
      .uploadDocument(document, file, false, this.entrySummary.companyId)
      .pipe(switchMap(() => this.loadDocuments(false)));
  }

  public uploadDocumentCall(document: DocumentCreateModel, file: File): void {
    this.uploadDocument(document, file).pipe(takeUntil(this.destroy$)).subscribe();
  }

  public removeDocument(documentId: number): void {
    this.entrySummaryStateService
      .removeDocument(documentId)
      .pipe(
        switchMap(() => this.loadDocuments(false)),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  public openBLBody(): void {
    this.isBodyOpen = !this.isBodyOpen;
    this.isShowAddInvoiceButton = true;
  }

  public onDeleteBL() {
    this.removeBl.emit();
    this.isBLConfirmModalOpen = false;
  }

  public trackByInvoiceFn(i: InvoiceModel): number {
    return i.id;
  }

  public get isAnyBlDetailsInvalid(): boolean {
    return (
      // #13047: Master BL number is optional for Truck transportation mode
      (!this.isTruck && !this.shipment.masterBill?.number) ||
      !this.shipment.masterBill?.carrier ||
      (this.shipment.houseBill?.carrier && !this.shipment.houseBill?.number) ||
      (this.shipment.houseBill?.number && !this.shipment.houseBill?.carrier) ||
      (this.entrySummary.portOfEntryCode !== this.entrySummary.portOfUnladingCode && (!this.shipment.itDate || !this.shipment.itNumber)) ||
      !this.shipment?.quantity ||
      !this.shipment?.quantityUnit
    );
  }

  public get isShowInfo(): boolean {
    return (
      this.entrySummary &&
      this.entrySummary.state === enumEntrySummaryState.NOT_SENT &&
      this.entrySummary.paymentState !== enumPaymentState.PENDING
    );
  }

  public get isSupportOrAdmin(): boolean {
    return this.companiesService.isSupportOrAdmin();
  }

  public get isActiveSupportMode() {
    return this.applicationModeService.activeApplicationMode === enumApplicationMode.SUPPORT;
  }

  public get isAllTermsFieldsMissing(): boolean {
    return this.isHasEsOcrRedesign && !this.shipment.freightCost && !this.shipment.transportationCharge && !this.shipment.insurance;
  }

  public get isHasEsOcrRedesign(): boolean {
    return this.companiesService
      .getUser()
      ?.betaFlags?.map((flag: UserBetaFlagModel) => flag.tag)
      .includes(enumUserBetaFlag.esOcrRedesign);
  }

  public get blQuantityErrorText(): string {
    if (this.findShipmentError(QUANTITY_MISSING)) {
      return 'BACK_ERROR.QUANTITY_MISSING';
    }
  }

  public get blQuantityUnitErrorText(): string {
    if (this.findShipmentError(QUANTITY_UNIT_MISSING)) {
      return 'BACK_ERROR.QUANTITY_UNIT_MISSING';
    }
  }

  public get hasBlMasterCarrierErrorText(): boolean {
    return !!this.findShipmentError(MASTER_BILL_CARRIER_WRONG_LENGTH);
  }

  public get hasBlHouseCarrierErrorText(): boolean {
    return !!this.findShipmentError(HOUSE_BILL_CARRIER_WRONG_LENGTH);
  }

  public get isRail(): boolean {
    return this.transportationMode === enumTransportationMode.RAIL;
  }

  /** #13499 — invoice ordinals are shown only when the BL carries more than one invoice. */
  public get isMultiInvoice(): boolean {
    return !!this.shipment?.invoices && this.shipment.invoices.length > 1;
  }

  public get blOrdinalSuffix(): string {
    return this.blOrdinal > 0 ? ' #' + this.blOrdinal : '';
  }

  public get isTruck(): boolean {
    return this.transportationMode === enumTransportationMode.TRUCK;
  }

  public joinedInvoice(current: InvoiceModel, formData: InvoiceModel): InvoiceModel {
    return { ...current, ...formData };
  }

  public checkInvoicesOpenStates(event, index): void {
    this.invoicesOpenStates[index] = event;
    this.checkAddInvoiceButtonState();
  }

  public removeInvoice(invoice, index): void {
    this.removeInvoiceSource.emit({ invoiceId: invoice.id, shipmentId: this.shipment.id });
    this.invoicesOpenStates.splice(index, 1);
  }

  public detectChanges(): void {
    this.changeDetector.detectChanges();
  }

  public onAddInvoice() {
    const data = {
      document: {
        id: 1, //default bl invoice id
        rootEntityId: this.entrySummary.id,
        rootEntityType: enumDocumentRootEntityType.ENTRY_SUMMARY,
        relatedEntityId: this.shipment.id,
        relatedEntityType: enumDocumentRelatedEntityType.SHIPMENT,
        url: null,
        createdDate: null,
        fileName: null,
        type: enumDocumentType.INVOICE,
        state: null,
      },
      shipmentId: this.shipment.id,
      isForceAdd: true,
    };

    this.entrySummaryStateService.uploadDocStripDocument(data);
  }

  private updateInvoiceOpenStates(): void {
    this.shipment.invoices.forEach((invoice: InvoiceModel, _index: number) => {
      this.invoicesOpenStates[_index] = this.invoicesOpenStates[_index] ? this.invoicesOpenStates[_index] : invoice.lines.length === 0;
    });
    this.checkAddInvoiceButtonState();
  }

  private checkAddInvoiceButtonState(): void {
    this.isShowAddInvoiceButton = this.invoicesOpenStates.every((state: boolean, _index: number) => {
      return state === false;
    });
  }

  private findShipmentError(field: string) {
    const errors = this.shipment?.validationMessages || [];

    return errors.find((item) => item.text === field)?.text;
  }

  private updateBlEnteredValue() {
    this.blEnteredValue = 0;

    this.entrySummaryService
      .getShipmentEnteredValue(this.shipment)
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => (this.blEnteredValue = value));
  }

  private updateLabelsData() {
    this.currentLabelsData = this.esLabelsService.getLabelsData(this.transportationMode);
  }

  /**
   * #13499 — BL ordinal is derived from `entrySummary.shipments` (not from the parent's *ngFor index) so the client
   * page's filtered `currentShipments` during invoice creation cannot shift the numbers; no page has to pass an index.
   */
  private updateBlOrdinal(): void {
    const shipments: ShipmentModel[] = this.entrySummary?.shipments || [];
    if (shipments.length < 2 || !this.shipment) {
      this.blOrdinal = null;
      return;
    }

    const index: number = shipments.findIndex((item: ShipmentModel) => item.id === this.shipment.id);
    this.blOrdinal = index === -1 ? null : index + 1;
  }

  private loadDocuments(isShowLoader: boolean) {
    return this.entrySummaryStateService.updateDocuments(isShowLoader).pipe(
      takeUntil(this.destroy$),
      tap((documents) => {
        this.blDoc = null;
        this.attachedDocuments = documents;
        this.prepareInvoiceDocuments();

        this.detectChanges();
      }),
    );
  }

  private findAttachedDocument() {
    return this.attachedDocuments.find(
      (document) =>
        document.relatedEntityType === enumDocumentRelatedEntityType.SHIPMENT &&
        document.type === enumDocumentType.BILL_OF_LADING &&
        document.relatedEntityId === this.shipment.id,
    );
  }

  private prepareInvoiceDocuments(): void {
    const blDoc = this.findAttachedDocument() || {
      relatedEntityId: this.shipment.id,
      type: enumDocumentType.BILL_OF_LADING,
      relatedEntityType: enumDocumentRelatedEntityType.SHIPMENT,
      rootEntityType: enumDocumentRootEntityType.ENTRY_SUMMARY,
      rootEntityId: this.entrySummary?.id,
      fileName: null,
      url: null,
      createdDate: null,
      id: null,
      state: enumDocumentState.CREATED,
    };

    this.blDoc = this.mergeDocument(this.blDoc, blDoc);
  }

  private mergeDocument(existedDocument: DocumentModel, newDocument: DocumentModel): DocumentModel {
    if (!existedDocument) {
      return newDocument;
    }
    Object.assign(existedDocument, newDocument);
    return existedDocument;
  }

  private loadAllValidationMessages() {
    this.allValidationMessages = [];
    this.termsValidationMessages = [];
    this.entrySummaryService
      .getShipmentValidationMessagesErrorData(this.shipment, this.transportationMode)
      .pipe(takeUntil(this.destroy$))
      .subscribe((messages) => {
        this.allValidationMessages = messages;
        this.isBlDetailsEmpty = this.entrySummaryValidationService.isBlDetailsEmpty(this.shipment);
        this.checkIsMissing();
        this.checkIsMissingTerms();
      });
  }

  private checkIsMissing() {
    this.shipmentValidationMessages = [];

    // #11228
    const blMissingFieldsToExclude = [
      QUANTITY_MISSING, // #11915
      QUANTITY_UNIT_MISSING, // #11915
      MASTER_BILL_NUMBER_MISSING,
      MASTER_BILL_CARRIER_MISSING,
      MASTER_AIR_BILL_PREFIX_MISSING,
      FREIGHT_COST_MISSING, //#11700
      FREIGHT_COST_LESS_THAN_LINES_COUNT, //#11700
    ];

    for (const mess of this.allValidationMessages) {
      if (
        blMissingFields.filter((error) => !blMissingFieldsToExclude.includes(error)).some((error) => mess.text.endsWith(error)) ||
        (this.hasSingleExportData && blMissingFieldsSpec.some((error) => mess.text.endsWith(error)))
      ) {
        this.shipmentValidationMessages.push(mess);
      }
    }

    this.checkIsMissingSeller();
    this.checkIsMissingConsignee();
    this.checkIsMissingBuyer();
  }

  private checkIsMissingSeller() {
    const allValidationMessages = EntrySummaryValidationHelper.getValidationMessages(this.shipment.seller, this.transportationMode);
    this.isSellerMissed =
      this.shipment?.validationMessages.map((item) => item.text).some((error: string) => error.endsWith(SELLER_MISSING)) ||
      !this.shipment.seller;
  }

  private checkIsMissingConsignee() {
    const allValidationMessages = EntrySummaryValidationHelper.getValidationMessages(this.shipment.consignee, this.transportationMode);

    this.isConsigneeMissed =
      this.shipment?.validationMessages.map((item) => item.text).some((error: string) => error.endsWith(CONSIGNEE_MISSING)) ||
      !this.shipment.consignee;
  }

  private checkIsMissingBuyer() {
    const allValidationMessages = EntrySummaryValidationHelper.getValidationMessages(this.shipment.buyer, this.transportationMode);

    this.isBuyerMissed =
      this.shipment?.validationMessages.map((item) => item.text).some((error: string) => error.endsWith(BUYER_MISSING)) ||
      !this.shipment.buyer;

    this.buyerValidationMessages = [];
    for (const mess of allValidationMessages) {
      if (blBuyerMissingFields.some((error) => mess.text.endsWith(error))) {
        this.buyerValidationMessages.push(mess);
      }
    }
  }

  // private isHTSOpen(invoice: InvoiceModel): boolean {
  //   return (
  //     this.esStepsService.isCreatingInvoice ||
  //     this.isBLNewlyCreated ||
  //     (this.newlyCreatedInvoicesIds || []).includes(invoice.id) ||
  //     !invoice.lines.length ||
  //     invoice.lines.some((line) => !line.value || !line.grossWeight) ||
  //     (!this.esStepsService.isCreating &&
  //       !this.esStepsService.isCreatingInvoice &&
  //       !this.esStepsService.creatingInvoiceBLId &&
  //       invoice.lines.some((item) => item.validationMessages?.length > 0))
  //   );
  // }

  private checkIsMissingTerms() {
    const termsMissingFields = [FREIGHT_COST_MISSING, FREIGHT_COST_LESS_THAN_LINES_COUNT];

    for (const mess of this.allValidationMessages) {
      if (termsMissingFields.some((error) => mess.text.endsWith(error))) {
        this.termsValidationMessages.push(mess);
      }
    }
  }
}
